import React, { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import NotificationBell from './bell';
import NotificationList from '.';

interface NotificationsMenuProps {
  companyName?: string;
  userId: any;
  dismissAll: (userId: any) => void;
  onNavigate: (path: string) => void;
}

const NotificationsMenu: React.FC<NotificationsMenuProps> = ({ companyName, userId, dismissAll, onNavigate }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [markAllVersion, setMarkAllVersion] = useState(0);
  // A quick action's confirm dialog renders in its own portal. Without this the
  // first click inside it counts as "outside the popover" and closes both.
  const interactionLockedRef = useRef(false);
  const handleInteractionLockChange = useCallback((locked: boolean) => {
    interactionLockedRef.current = locked;
  }, []);
  const keepOpenWhileLocked = useCallback((event: Event | KeyboardEvent) => {
    if (interactionLockedRef.current) event.preventDefault();
  }, []);

  const handleDismissAll = async () => {
    try {
      const maybeThunk = dismissAll(userId);
      if (typeof maybeThunk === 'function') {
        await (dispatch as any)(maybeThunk);
      }
      setMarkAllVersion(v => v + 1);
    } catch (e) {
      console.error('Dismiss all failed', e);
    }
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="rounded-lg transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t('Notifications') || 'Notifications'}
          title={t('Notifications')}
        >
          <NotificationBell />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          onPointerDownOutside={keepOpenWhileLocked}
          onFocusOutside={keepOpenWhileLocked}
          onInteractOutside={keepOpenWhileLocked}
          onEscapeKeyDown={keepOpenWhileLocked}
          className="w-[340px] sm:w-[420px] md:w-[480px] max-w-[90vw] max-h-[560px] flex flex-col rounded-lg bg-card border border-border shadow-lg z-50 focus:outline-none"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 pb-0">
            <span className="text-sm font-semibold text-foreground">{t('Notifications')}</span>
            <Popover.Close asChild>
              <button className="p-1 rounded hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" aria-label={t('Close') || 'Close'}>
                <Icon name="X" size={16} className="text-muted-foreground" />
              </button>
            </Popover.Close>
          </div>

          <div className="border-t border-border mt-2" />

          {/* Actions */}
          <div className="flex gap-2 px-3 py-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => { onNavigate(`/${companyName}/notifications`); setOpen(false); }}>
              {t('View all')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDismissAll}>
              {t('Dismiss all')}
            </Button>
          </div>

          <div className="border-t border-border" />

          {/* Content */}
          <div className="flex-1 overflow-y-auto py-2 px-3 relative">
            <NotificationList markAllReadSignal={markAllVersion} onInteractionLockChange={handleInteractionLockChange} />
            <div className="sticky bottom-0 inset-x-0 h-4 mt-2 bg-gradient-to-b from-transparent to-card pointer-events-none" />
          </div>

          <div className="border-t border-border" />

          {/* Footer */}
          <div className="p-2 text-center">
            <span className="text-xs text-muted-foreground">{t('Updated just now')}</span>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default NotificationsMenu;
